/**
 * PathPal Clean Product Page — pp-product-page.js
 * Handles: variant selection, add-to-cart (with diameter popup for single pathpal),
 *          FAQ accordion, comparison expand, sticky CTA, scroll animations, toast
 */

(function () {
  'use strict';

  /* --------------------------------------------------
     SHARED STATE
  -------------------------------------------------- */
  const state = {
    selectedVariantId: null,
    selectedVariantPrice: 0,
    selectedVariantName: '',
    selectedPackageType: '',
    productData: null,
  };

  /* --------------------------------------------------
     HELPERS
  -------------------------------------------------- */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function showToast(message) {
    let toast = document.getElementById('pp-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'pp-toast';
      toast.className = 'pp-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg> ${message}`;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 2500);
  }

  function track(eventName, params = {}) {
    if (!eventName) return;
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });
  }

  /* --------------------------------------------------
     PRODUCT DATA
  -------------------------------------------------- */
  function loadProductData() {
    const el = document.getElementById('pp-product-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('pathpal: could not parse product data', e);
      return null;
    }
  }

  /* --------------------------------------------------
     VARIANT SELECTOR
  -------------------------------------------------- */
  function initVariantSelector() {
    state.productData = loadProductData();
    if (!state.productData) return;

    const cards = document.querySelectorAll('.pp-variant-card');
    if (!cards.length) return;

    cards.forEach(card => {
      card.addEventListener('click', () => selectVariant(card));
      card.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectVariant(card);
        }
      });
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'radio');
    });

    // Select the default card
    const defaultCard = document.querySelector('.pp-variant-card[data-default="true"]')
      || document.querySelector('.pp-variant-card');
    if (defaultCard) selectVariant(defaultCard, false);
  }

  function selectVariant(card, animate) {
    // Update visual state
    document.querySelectorAll('.pp-variant-card').forEach(c => {
      c.classList.remove('is-selected');
      c.setAttribute('aria-checked', 'false');
    });
    card.classList.add('is-selected');
    card.setAttribute('aria-checked', 'true');

    const variantId   = card.dataset.variantId;
    const price       = parseInt(card.dataset.price, 10);
    const name        = card.dataset.name;
    const packageType = card.dataset.packageType || '';

    state.selectedVariantId    = variantId;
    state.selectedVariantPrice = price;
    state.selectedVariantName  = name;
    state.selectedPackageType  = packageType;

    if (animate !== false) {
      track('variant_select', {
        variant_name: name,
        variant_price: price / 100,
      });
    }

    // Update hidden form input
    const formInput = document.getElementById('pp-variant-id-input');
    if (formInput) formInput.value = variantId;

    // Update price display (with fade)
    const priceEl = document.getElementById('pp-price-display');
    if (priceEl) {
      if (animate !== false) {
        priceEl.style.opacity = '0';
        setTimeout(() => {
          priceEl.textContent = formatMoney(price);
          priceEl.style.opacity = '1';
        }, 150);
      } else {
        priceEl.textContent = formatMoney(price);
      }
    }

    // Always show "Add to Cart" — never include price in button label
    const addBtn = document.getElementById('pp-add-to-cart-btn');
    if (addBtn) addBtn.textContent = 'Add to Cart';

    // Update sticky CTA
    const stickyName  = document.getElementById('pp-sticky-name');
    const stickyPrice = document.getElementById('pp-sticky-price');
    const stickyBtn   = document.getElementById('pp-sticky-btn');
    if (stickyName)  stickyName.textContent = name;
    if (stickyPrice) stickyPrice.textContent = formatMoney(price);
    if (stickyBtn)   stickyBtn.textContent   = 'Add to Cart';
  }

  /* --------------------------------------------------
     DIAMETER POPUP (single pathpal variant)
  -------------------------------------------------- */
  let diameterPopupVariantId = null; // resolved after user picks size

  function getSinglePathpalVariants() {
    if (!state.productData) return [];
    return state.productData.variants.filter(v => v.option1 === 'pathpal only');
  }

  function showDiameterPopup(btn) {
    const variants = getSinglePathpalVariants();
    if (!variants.length) return;

    const popup    = document.getElementById('pp-diameter-popup');
    const optionEl = document.getElementById('pp-diameter-options');
    const confirm  = document.getElementById('pp-diameter-confirm');
    if (!popup || !optionEl || !confirm) return;

    // Build option cards from variant data
    optionEl.innerHTML = '';
    diameterPopupVariantId = null;
    confirm.disabled = true;
    confirm.textContent = 'Select a size above to continue';

    variants.forEach(v => {
      const size = v.option2 || v.title; // option2 is the diameter
      const is38 = size && size.includes('3/8');
      const label = is38
        ? 'Standard golf alignment sticks (rubber/plastic tip at the bottom)'
        : 'Driveway marker style sticks from hardware stores — no rubber tip';

      const card = document.createElement('div');
      card.className = 'pp-diameter-option';
      card.dataset.variantId = v.id;
      card.innerHTML = `<div class="pp-diameter-option__size">${size || v.title}</div>
                        <div class="pp-diameter-option__label">${label}</div>`;

      card.addEventListener('click', () => {
        optionEl.querySelectorAll('.pp-diameter-option').forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        diameterPopupVariantId = v.id;
        confirm.disabled = false;
        confirm.textContent = 'Add to Cart';
      });

      optionEl.appendChild(card);
    });

    // If only one variant for single pathpal, skip popup and add directly
    if (variants.length === 1) {
      addToCart(variants[0].id, btn, 'single_pathpal');
      return;
    }

    popup.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Wire up confirm
    confirm.onclick = () => {
      if (!diameterPopupVariantId) return;
      closeDiameterPopup();
      addToCart(diameterPopupVariantId, btn, 'single_pathpal');
    };
  }

  function closeDiameterPopup() {
    const popup = document.getElementById('pp-diameter-popup');
    if (popup) popup.style.display = 'none';
    document.body.style.overflow = '';
    diameterPopupVariantId = null;
  }

  function initDiameterPopup() {
    const closeBtn = document.getElementById('pp-diameter-close');
    if (closeBtn) closeBtn.addEventListener('click', closeDiameterPopup);

    const popup = document.getElementById('pp-diameter-popup');
    if (popup) {
      popup.addEventListener('click', e => {
        if (e.target === popup) closeDiameterPopup();
      });
    }

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeDiameterPopup();
    });
  }

  /* --------------------------------------------------
     ADD TO CART
  -------------------------------------------------- */
  function initAddToCart() {
    // Main buy box button
    const addBtn = document.getElementById('pp-add-to-cart-btn');
    if (addBtn) {
      addBtn.addEventListener('click', () => {
        // If single pathpal, show diameter selection first
        if (state.selectedPackageType === 'pathpal only') {
          const singleVariants = getSinglePathpalVariants();
          if (singleVariants.length > 1) {
            showDiameterPopup(addBtn);
            return;
          }
        }
        addToCart(state.selectedVariantId, addBtn, 'buybox');
      });
    }

    // Sticky CTA button
    const stickyBtn = document.getElementById('pp-sticky-btn');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        track('sticky_cta_click', { variant_name: state.selectedVariantName });
        if (state.selectedPackageType === 'pathpal only') {
          const singleVariants = getSinglePathpalVariants();
          if (singleVariants.length > 1) {
            showDiameterPopup(stickyBtn);
            return;
          }
        }
        addToCart(state.selectedVariantId, stickyBtn, 'sticky_cta');
      });
    }

    // Final CTA buttons (direct-add)
    document.querySelectorAll('[data-pp-add-variant]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.ppAddVariant;
        const source = btn.dataset.ppSource || 'direct_cta';
        if (id) addToCart(id, btn, source);
      });
    });
  }

  function addToCart(variantId, btn, source) {
    if (!variantId) {
      showToast('Please select an option.');
      return;
    }

    const originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then(res => {
        if (!res.ok) throw new Error('Cart error');
        return res.json();
      })
      .then(item => {
        const itemPrice = (item && item.price ? item.price : state.selectedVariantPrice) ? (item && item.price ? item.price : state.selectedVariantPrice) / 100 : undefined;
        const itemName = (item && item.product_title) || state.selectedVariantName || btn.getAttribute('aria-label') || btn.textContent.trim();
        track('add_to_cart', {
          item_id: variantId,
          item_name: itemName,
          price: itemPrice,
          value: itemPrice,
          currency: 'USD',
          source: source || 'unknown',
          transport_type: 'beacon',
        });
        setTimeout(() => {
          window.location.href = '/cart';
        }, 180);
      })
      .catch(() => {
        btn.textContent = originalLabel;
        btn.disabled = false;
        showToast('Something went wrong. Please try again.');
      });
  }

  function refreshCartCount() {
    // Use Shopify Sections Rendering API to update cart icon bubble
    fetch('/?sections=cart-icon-bubble', { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(data => {
        const el = document.getElementById('cart-icon-bubble');
        if (el && data['cart-icon-bubble']) {
          el.innerHTML = data['cart-icon-bubble'];
        }
      })
      .catch(() => {});
  }

  /* --------------------------------------------------
     FAQ ACCORDION
  -------------------------------------------------- */
  function initFAQ() {
    const items = document.querySelectorAll('.pp-faq-item');
    if (!items.length) return;

    items.forEach(item => {
      const btn  = item.querySelector('.pp-faq-item__btn');
      const body = item.querySelector('.pp-faq-item__body');
      if (!btn || !body) return;

      const bodyId = 'faq-body-' + Math.random().toString(36).slice(2, 7);
      body.id = bodyId;
      btn.setAttribute('aria-controls', bodyId);
      btn.setAttribute('aria-expanded', 'false');

      btn.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        // Close all
        items.forEach(i => {
          i.classList.remove('is-open');
          const b = i.querySelector('.pp-faq-item__btn');
          if (b) b.setAttribute('aria-expanded', 'false');
        });

        // Open clicked (toggle)
        if (!isOpen) {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
          track('faq_open', { question_text: btn.textContent.trim() });
        }
      });
    });
  }

  /* --------------------------------------------------
     COMPARISON EXPAND/COLLAPSE
  -------------------------------------------------- */
  function initComparison() {
    const trigger = document.getElementById('pp-comparison-trigger');
    const drawer  = document.getElementById('pp-comparison-deep');
    if (!trigger || !drawer) return;

    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', 'pp-comparison-deep');

    trigger.addEventListener('click', () => {
      const isOpen = drawer.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (isOpen) track('comparison_expand');
      trigger.textContent = isOpen
        ? 'Hide details ↑'
        : 'See full feature-by-feature comparison ↓';
    });
  }

  /* --------------------------------------------------
     MOBILE STICKY CTA
  -------------------------------------------------- */
  function initStickyCTA() {
    const stickyCTA = document.getElementById('pp-sticky-cta');
    const buybox    = document.getElementById('shop');
    const footer    = document.querySelector('.pp-footer');
    if (!stickyCTA) return;

    let hasTrackedShow = false;

    function checkVisibility() {
      const scrollY      = window.scrollY;
      const pageHeight   = document.body.scrollHeight;
      const viewHeight   = window.innerHeight;
      const scrollPct    = scrollY / (pageHeight - viewHeight);

      // Hide near footer
      const footerTop = footer ? footer.getBoundingClientRect().top + scrollY : pageHeight;
      const nearFooter = (scrollY + viewHeight) > footerTop - 80;

      // Show after 40% scroll or past buybox
      const pastBuybox = buybox
        ? buybox.getBoundingClientRect().bottom + scrollY < scrollY
        : scrollPct > 0.15;

      const shouldShow = (scrollPct >= 0.4 || pastBuybox) && !nearFooter && window.innerWidth < 768;
      stickyCTA.classList.toggle('is-visible', shouldShow);
      if (shouldShow && !hasTrackedShow) {
        track('sticky_cta_show');
        hasTrackedShow = true;
      }
    }

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          checkVisibility();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    checkVisibility();
  }

  /* --------------------------------------------------
     SCROLL REVEAL ANIMATIONS
  -------------------------------------------------- */
  function initScrollReveal() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const els = document.querySelectorAll('.pp-reveal');
    if (!els.length) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    els.forEach(el => observer.observe(el));
  }

  /* --------------------------------------------------
     VIDEO POSTER CLICK-TO-PLAY
  -------------------------------------------------- */
  function initVideoPosters() {
    document.querySelectorAll('.pp-demo-video[data-video-src]').forEach(wrapper => {
      const poster = wrapper.querySelector('.pp-demo-video__poster');
      if (!poster) return;

      wrapper.addEventListener('click', () => {
        const src = wrapper.dataset.videoSrc;
        if (!src) return;

        const video = document.createElement('video');
        video.src = src;
        video.controls = true;
        video.autoplay = true;
        video.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;';
        poster.style.display = 'none';
        wrapper.appendChild(video);
        track('demo_video_play', {
          video_id: src.split('/').pop(),
          source: 'how_it_works',
        });
        video.addEventListener('ended', () => {
          track('demo_video_complete', {
            video_id: src.split('/').pop(),
            source: 'how_it_works',
          });
        }, { once: true });
        video.play().catch(() => {});
      }, { once: true });
    });
  }

  /* --------------------------------------------------
     UPSELL CTA (Complete System section)
  -------------------------------------------------- */
  function initUpsellCTA() {
    const btn = document.getElementById('pp-upsell-add-btn');
    if (!btn) return;
    btn.addEventListener('click', () => {
      const id = btn.dataset.variantId;
      track('upsell_cta_click', { price: 175 });
      if (id) addToCart(id, btn, 'upsell');
    });
  }

  function initTrackedClicks() {
    document.querySelectorAll('[data-pp-track]').forEach(el => {
      el.addEventListener('click', () => {
        const eventName = el.dataset.ppTrack;
        const params = {};
        if (el.dataset.ppTrackLabel) params.cta_label = el.dataset.ppTrackLabel;
        if (el.dataset.ppTrackSource) params.source = el.dataset.ppTrackSource;
        track(eventName, params);
      });
    });
  }

  function initScrollDepthTracking() {
    const marks = [25, 50, 75, 100];
    const seen = new Set();

    function checkDepth() {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll <= 0) return;
      const pct = Math.min(100, Math.round((window.scrollY / maxScroll) * 100));
      marks.forEach(mark => {
        if (pct >= mark && !seen.has(mark)) {
          seen.add(mark);
          track(`scroll_${mark}`);
        }
      });
    }

    window.addEventListener('scroll', checkDepth, { passive: true });
    checkDepth();
  }

  /* --------------------------------------------------
     SMOOTH SCROLL (anchor clicks)
  -------------------------------------------------- */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(link => {
      link.addEventListener('click', e => {
        const targetId = link.getAttribute('href').slice(1);
        const target = document.getElementById(targetId);
        if (!target) return;
        e.preventDefault();
        const offset = 96; // sticky header height
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  }

  /* --------------------------------------------------
     INIT
  -------------------------------------------------- */
  function init() {
    const hasLegacyBuybox = !!document.getElementById('pp-product-data');
    initAddToCart();
    if (hasLegacyBuybox) {
      initVariantSelector();
      initDiameterPopup();
      initStickyCTA();
    } else {
      // Still wire any standalone [data-pp-add-variant] buttons (e.g. pp-final-cta)
      document.querySelectorAll('[data-pp-add-variant]').forEach(btn => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.ppAddVariant;
          const source = btn.dataset.ppSource || 'direct_cta';
          if (id) addToCart(id, btn, source);
        });
      });
    }
    initFAQ();
    initComparison();
    initScrollReveal();
    initVideoPosters();
    initUpsellCTA();
    initTrackedClicks();
    initScrollDepthTracking();
    initSmoothScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
